/**
 * Ed Hardy Swim — shoppable grid with filtering and sorting.
 *
 * Filters are derived from product titles (category, colour) because the
 * store's tags carry drop names only. Sold-out and last-size products are
 * hidden by default, which removes roughly a third of the swim catalogue from
 * view — those are the products that make the site feel picked over.
 */

import {
  loadCatalog,
  bestsellerRank,
  sorter,
  FAMILY_SWATCHES,
  CATEGORY_LABELS,
  groupOf,
  catsInGroup,
  explodeColourways,
} from '@eh/catalog';
import { cardHTML, bindQuickAdd, sized } from '@eh/card';

const root = () => window.Shopify?.routes?.root || '/';

/* Letter sizes first in run order, then the numeric kids sizes in numeric
   order, then anything unrecognised. */
const LETTER_ORDER = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL', 'XXL', 'OS'];

function sizeRank(s) {
  const t = String(s).trim().toUpperCase();
  const letter = LETTER_ORDER.findIndex((l) => t === l || t.startsWith(`${l}(`) || t.startsWith(`${l} (`));
  if (letter > -1) return letter;
  const num = parseInt(t, 10);
  if (!Number.isNaN(num)) return 100 + num;
  return 999;
}

class EhShop extends HTMLElement {
  async connectedCallback() {
    this.cfg = JSON.parse(this.querySelector('[data-eh-config]').textContent);

    /*
     * Filters are mirrored into the URL, so any view of this page is a real,
     * shareable link — /collections/shop-all-swim?cat=bottom drops straight
     * into bikini bottoms. That makes every category directly linkable from an
     * ad, an email or a story.
     */
    const q = new URLSearchParams(location.search);
    this.state = {
      cat: q.get('cat') ? groupOf(q.get('cat')) : 'all',
      colour: q.get('colour') || q.get('color') || 'all',
      size: q.get('size') || 'all',
      sort: q.get('sort') || 'bestselling',
      inStockOnly: q.get('instock') ? q.get('instock') !== '0' : this.cfg.hideThin !== false,
      page: 1,
    };
    this.perPage = this.cfg.perPage || 24;

    this.filtersEl = this.querySelector('[data-eh-filters]');
    this.gridEl = this.querySelector('[data-eh-grid]');
    this.countEl = this.querySelector('[data-eh-count]');
    this.moreEl = this.querySelector('[data-eh-more]');

    this.moreEl.addEventListener('click', () => {
      this.state.page += 1;
      this.paint();
    });

    const [catalog, rank, members] = await Promise.all([
      loadCatalog(),
      bestsellerRank(this.cfg.rankFrom || this.cfg.collection || 'shop-all-swim'),
      this.membership(),
    ]);
    this.rank = rank;

    /* Scope: exact collection membership when we have it, else by category. */
    if (members && members.size) {
      this.pool = catalog.filter((p) => members.has(p.handle));
    } else if (this.cfg.cats && this.cfg.cats.length) {
      this.pool = catalog.filter((p) => this.cfg.cats.includes(p.cat));
    } else {
      this.pool = catalog.filter((p) => p.cat !== 'other');
    }

    /*
     * Bags are single listings with the prints as variants, so Beach Bags read
     * as four products when there are twenty-four bags to look at. Split them
     * so every colourway gets a tile of its own.
     */
    this.pool = this.pool.flatMap((p) => (p.cat === 'bag' ? explodeColourways(p) : [p]));

    /* Only strip kids when the grid is category-scoped. A kids collection is
       explicitly scoped and must keep its own products. */
    if (this.cfg.excludeKids && !(members && members.size)) {
      this.pool = this.pool.filter((p) => p.cat !== 'kids');
    }

    this.buildFilters();
    this.paint();
  }

  /**
   * Exact membership for the collection this grid represents. Liquid can only
   * expose 50 products per page, so the handle list is pulled from the
   * collection's own products.json (one cached request, up to 250 handles).
   */
  async membership() {
    if (!this.cfg.collection) return null;
    try {
      const r = await fetch(`${root()}collections/${this.cfg.collection}/products.json?limit=250`);
      if (!r.ok) return null;
      const d = await r.json();
      return new Set((d.products || []).map((p) => p.handle));
    } catch (e) {
      return null;
    }
  }

  /** Mirror the current filters into the address bar without adding history. */
  syncUrl() {
    const q = new URLSearchParams(location.search);
    const put = (k, v, dflt) => (v && v !== dflt ? q.set(k, v) : q.delete(k));
    put('cat', this.state.cat, 'all');
    put('colour', this.state.colour, 'all');
    put('size', this.state.size, 'all');
    put('sort', this.state.sort, 'bestselling');
    q.delete('color');
    if (this.state.inStockOnly === (this.cfg.hideThin !== false)) q.delete('instock');
    else q.set('instock', this.state.inStockOnly ? '1' : '0');
    const qs = q.toString();
    history.replaceState(null, '', qs ? `${location.pathname}?${qs}` : location.pathname);
  }

  matching(ignore) {
    return this.pool.filter((p) => {
      if (ignore !== 'stock' && this.state.inStockOnly && p.thin) return false;
      if (
        ignore !== 'cat' &&
        this.state.cat !== 'all' &&
        !catsInGroup(this.state.cat).includes(p.cat)
      ) {
        return false;
      }
      if (ignore !== 'colour' && this.state.colour !== 'all' && p.family !== this.state.colour)
        return false;
      if (ignore !== 'size' && this.state.size !== 'all' && !p.sizes.includes(this.state.size))
        return false;
      return true;
    });
  }

  /**
   * A representative image for a category circle: the best-selling in-stock
   * product *of that category*, so Cover Ups shows a cover-up and Bikini Tops
   * shows a top. `used` keeps the All circle from duplicating a category.
   */
  categoryImage(cat, used) {
    const ranked = this.pool
      .filter((p) => (cat ? catsInGroup(cat).includes(p.cat) : true) && !p.thin && p.image)
      .sort(sorter(this.rank || new Map()));
    const fresh = ranked.find((p) => !used || !used.has(p.image));
    const pick =
      fresh || ranked[0] || this.pool.find((p) => catsInGroup(cat).includes(p.cat) && p.image);
    if (pick && used) used.add(pick.image);
    return pick ? pick.image : '';
  }

  buildFilters() {
    const cats = [...new Set(this.pool.map((p) => groupOf(p.cat)))].filter((c) => CATEGORY_LABELS[c]);
    const colours = [...new Set(this.pool.map((p) => p.family))].filter(
      (f) => f !== 'other' && FAMILY_SWATCHES[f]
    );
    const sizes = [...new Set(this.pool.flatMap((p) => p.sizes))].sort(
      (a, b) => sizeRank(a) - sizeRank(b)
    );

    const usedImages = new Set();
    const laneImages = new Map(cats.map((c) => [c, this.categoryImage(c, usedImages)]));
    const allImg = this.categoryImage(null, usedImages);

    /* One category means the circles say nothing — a Beach Bags collection does
       not need an "All / Beach Bags" choice. */
    const showLanes = cats.length > 1;

    this.filtersEl.innerHTML = `
      ${showLanes ? '' : '<!-- single category, lane rail suppressed -->'}
      <div class="eh-shop__row"${showLanes ? '' : ' hidden'}>
        <ul class="eh-shop__lanes list-unstyled" role="list">
          <li>
            <button type="button" class="eh-lane${this.state.cat === 'all' ? ' is-active' : ''}" data-k="cat" data-v="all">
              <span class="eh-lane__ring"><span class="eh-lane__thumb">
                ${allImg ? `<img src="${sized(allImg, 200)}" alt="" loading="lazy" width="100" height="100">` : ''}
              </span></span>
              <span class="eh-lane__label">All</span>
            </button>
          </li>
          ${cats
            .map((c) => {
              const img = laneImages.get(c);
              return `<li>
                <button type="button" class="eh-lane${this.state.cat === c ? ' is-active' : ''}" data-k="cat" data-v="${c}">
                  <span class="eh-lane__ring"><span class="eh-lane__thumb">
                    ${img ? `<img src="${sized(img, 200)}" alt="" loading="lazy" width="100" height="100">` : ''}
                  </span></span>
                  <span class="eh-lane__label">${CATEGORY_LABELS[c]}</span>
                </button>
              </li>`;
            })
            .join('')}
        </ul>
      </div>

      <div class="eh-shop__row eh-shop__row--tools">
        ${
          colours.length
            ? `<div class="eh-shop__swatches" role="group" aria-label="Colour">
          <button type="button" class="eh-sw${this.state.colour === 'all' ? ' is-active' : ''}" data-k="colour" data-v="all" aria-label="All colours"><span class="eh-sw__all">All</span></button>
          ${colours
            .map(
              (f) =>
                `<button type="button" class="eh-sw${this.state.colour === f ? ' is-active' : ''}" data-k="colour" data-v="${f}" aria-label="${f}" title="${f}"><span style="background:${FAMILY_SWATCHES[f]}"></span></button>`
            )
            .join('')}
        </div>`
            : ''
        }

        <label class="eh-shop__select">
          <span class="visually-hidden">Size</span>
          <select data-k="size">
            <option value="all">All sizes</option>
            ${sizes.map((s) => `<option value="${s}">${s}</option>`).join('')}
          </select>
        </label>

        <label class="eh-shop__select">
          <span class="visually-hidden">Sort by</span>
          <select data-k="sort">
            <option value="bestselling">Bestselling</option>
            <option value="new">Newest</option>
            <option value="low">Price: low to high</option>
            <option value="high">Price: high to low</option>
          </select>
        </label>

        <label class="eh-shop__toggle">
          <input type="checkbox" data-k="inStockOnly" ${this.state.inStockOnly ? 'checked' : ''}>
          <span>Hide sold out &amp; last size</span>
        </label>
      </div>
    `;

    this.filtersEl.querySelectorAll('[data-k][data-v]').forEach((b) => {
      /* restore the active pill from the URL */
      if (this.state[b.dataset.k] === b.dataset.v) {
        this.filtersEl
          .querySelectorAll(`[data-k="${b.dataset.k}"][data-v]`)
          .forEach((x) => x.classList.toggle('is-active', x === b));
      }
      b.addEventListener('click', () => {
        this.state[b.dataset.k] = b.dataset.v;
        this.state.page = 1;
        this.filtersEl
          .querySelectorAll(`[data-k="${b.dataset.k}"][data-v]`)
          .forEach((x) => x.classList.toggle('is-active', x === b));
        this.syncUrl();
        this.paint();
      });
    });
    this.filtersEl.querySelectorAll('select[data-k]').forEach((sel) => {
      if (this.state[sel.dataset.k]) sel.value = this.state[sel.dataset.k];
      sel.addEventListener('change', () => {
        this.state[sel.dataset.k] = sel.value;
        this.state.page = 1;
        this.syncUrl();
        this.paint();
      });
    });
    const toggle = this.filtersEl.querySelector('input[data-k="inStockOnly"]');
    toggle.checked = this.state.inStockOnly;
    toggle.addEventListener('change', () => {
      this.state.inStockOnly = toggle.checked;
      this.state.page = 1;
      this.syncUrl();
      this.paint();
    });
  }

  sorted(items) {
    const s = this.state.sort;
    if (s === 'low') return [...items].sort((a, b) => a.price - b.price);
    if (s === 'high') return [...items].sort((a, b) => b.price - a.price);
    if (s === 'new')
      return [...items].sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt)));
    return [...items].sort(sorter(this.rank));
  }

  paint() {
    const all = this.sorted(this.matching());
    const shown = all.slice(0, this.state.page * this.perPage);

    this.countEl.textContent =
      all.length === 1 ? '1 product' : `${all.length} products`;

    if (!all.length) {
      this.gridEl.innerHTML = `<p class="eh-shop__empty">Nothing matches those filters right now. Try turning off a filter.</p>`;
      this.moreEl.hidden = true;
      return;
    }

    /* the first row is the LCP candidate, so it loads immediately */
    this.gridEl.innerHTML = shown
      .map((p, i) => cardHTML(p, { width: 500, eager: i < 4 }))
      .join('');
    /* exploded colourways carry a compound key, so match that first */
    bindQuickAdd(this.gridEl, (k) =>
      this.pool.find((x) => (x.key || x.handle) === k) || this.pool.find((x) => x.handle === k)
    );

    this.moreEl.hidden = shown.length >= all.length;
    this.moreEl.textContent = `Load more (${all.length - shown.length} left)`;
  }
}

if (!customElements.get('eh-shop')) customElements.define('eh-shop', EhShop);
